<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
});

import { onClickOutside } from '@vueuse/core';
import { watch, ref, useSlots, onUnmounted, computed } from 'vue';

import { AppSpinner } from '@/shared/ui/spinner';

import { OPACITY_FADE_TRANSITION } from './transitions';

// Определяем размеры модального окна
type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

// Определяем позиции модального окна
type ModalPosition = 'center' | 'top' | 'bottom';

interface Props {
  show?: boolean;
  // По умолчанию клик по затемнению не закрывает модалку — данные формы не теряются случайно.
  closeOnBackdrop?: boolean;
  loading?: boolean;
  size?: ModalSize;
  position?: ModalPosition;
  persistent?: boolean;
  fullScreen?: boolean;
  closeOnEscape?: boolean;
  // Поднимает z-index, чтобы модалка перекрывала другую BaseModal (например, диалог подтверждения).
  elevated?: boolean;
  // Спрашивает перед закрытием крестиком, Escape или фоном; `false` оставляет окно открытым.
  beforeClose?: () => boolean | Promise<boolean>;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  closeOnBackdrop: false,
  loading: false,
  size: 'md',
  position: 'center',
  persistent: false,
  fullScreen: false,
  closeOnEscape: true,
  elevated: false,
  beforeClose: undefined,
});

interface Emits {
  'update:show': [value: boolean];
  close: [];
  open: [];
}

const emit = defineEmits<Emits>();

const isOpen = ref(props.show);
const modalRef = ref<HTMLElement | null>(null);

// Размеры модального окна
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md lg:max-w-lg',
    lg: 'max-w-lg lg:max-w-2xl',
    xl: 'max-w-xl lg:max-w-4xl',
    full: 'max-w-full',
  };

  return sizes[props.size];
});

// Позиционирование модального окна
const positionClasses = computed(() => {
  const positions = {
    center: 'items-center justify-center min-h-full',
    top: 'items-start justify-center pt-16 min-h-full',
    bottom: 'items-end justify-center pb-16 min-h-full',
  };

  return positions[props.position];
});

// Классы для темизации через Tailwind dark: селектор
const themeClasses = {
  backdrop: 'bg-black/50 dark:bg-gray-900/75',
  modal: 'bg-white text-gray-900 dark:bg-gray-800 dark:text-white',
  closeButton: 'text-gray-primary hover:text-gray-700 dark:text-gray-300 dark:hover:text-white',
  title: 'text-gray-900 dark:text-white',
  border: 'border-gray-200 dark:border-gray-600',
};

// Классы для скролла и высоты
const scrollClasses = computed(() => {
  if (props.fullScreen) {
    return 'h-full max-h-full';
  }

  return '';
});

// Отслеживаем изменения prop show
watch(
  () => props.show,
  (newValue) => {
    isOpen.value = newValue;
  },
);

// Эмитим изменения isOpen
watch(isOpen, (newValue) => {
  emit('update:show', newValue);

  if (newValue) {
    emit('open');
  }
});

// Управляем скроллом body и эмитим событие закрытия
watch(
  isOpen,
  (newValue, oldValue) => {
    document.body.style.overflowY = newValue ? 'hidden' : 'visible';

    // Только закрытие открытой: модалка, смонтированная закрытой, close не шлёт —
    // иначе обработчик с навигацией (диалог на адресе) уводит со страницы.
    if (!newValue && oldValue) {
      emit('close');
    }
  },
  { immediate: true },
);

// Восстанавливаем скролл при размонтировании
onUnmounted(() => {
  document.body.style.overflowY = 'visible';
});

// Методы управления модальным окном. Пока ждём ответа `beforeClose`, повторный Escape не спрашивает снова.
let closing = false;
const close = async (): Promise<void> => {
  if (props.persistent || closing) return;

  if (!props.beforeClose) {
    isOpen.value = false;
    return;
  }

  closing = true;
  try {
    if (await props.beforeClose()) isOpen.value = false;
  } finally {
    closing = false;
  }
};

const openModal = (): void => {
  isOpen.value = true;
};

const toggle = (): void => {
  isOpen.value = !isOpen.value;
};

// Expose методы для родительского компонента
defineExpose({
  show: openModal,
  close,
  toggle,
});

// Обработка клика вне модального окна.
// Телепортированные слои помечают свой корень data-modal-ignore: клик по ним
// не закрывает модалку, хотя DOM-дерево находится вне modalRef.
if (props.closeOnBackdrop && !props.persistent) {
  onClickOutside(modalRef, () => void close(), { ignore: ['[data-modal-ignore]'] });
}

// Обработка нажатия Escape
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.closeOnEscape && !props.persistent) {
    void close();
  }
};

// Добавляем/удаляем обработчик события клавиатуры
watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('keydown', handleEscape);
  } else {
    document.removeEventListener('keydown', handleEscape);
  }
});

// Удаляем обработчик при размонтировании
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape);
});

const slots = useSlots();
</script>

<template>
  <Teleport to="#app">
    <!-- Backdrop -->
    <Transition v-bind="OPACITY_FADE_TRANSITION">
      <div
        v-if="isOpen"
        data-modal-ignore
        tabindex="-1"
        :class="['fixed inset-0 transition-opacity', elevated ? 'z-100000' : 'z-99998', themeClasses.backdrop]"
        @click="closeOnBackdrop && close()"
      />
    </Transition>

    <!-- Modal -->
    <Transition
      enter-active-class="transition-[opacity,scale] duration-[240ms] ease-snappy"
      leave-active-class="transition-[opacity,scale] duration-150 ease-snappy"
      enter-from-class="opacity-0 scale-[0.96] motion-reduce:scale-100"
      enter-to-class="opacity-100 scale-100"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-[0.98] motion-reduce:scale-100"
    >
      <div
        v-if="isOpen"
        :class="['fixed inset-0 overflow-y-auto', elevated ? 'z-100001' : 'z-99999', fullScreen ? '' : 'p-4 md:p-6']"
      >
        <div :class="['flex', positionClasses]">
          <div
            ref="modalRef"
            data-modal-ignore
            data-pull-to-refresh-ignore
            :class="[
              'relative w-full rounded-lg shadow-xl',
              'transition-[transform,opacity] duration-200',
              fullScreen ? 'h-full max-h-full max-w-full rounded-none' : sizeClasses,
              scrollClasses,
              themeClasses.modal,
              $attrs.class,
            ]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="slots.title ? 'modal-title' : undefined"
          >
            <!-- Close Button -->
            <button
              v-if="!persistent"
              type="button"
              :class="[
                'absolute top-2 right-2 z-50 flex h-11 w-11 items-center justify-center rounded-full',
                'transition-[transform,color] duration-200 hover:rotate-90 active:scale-[0.96]',
                'focus:ring-2 focus:ring-offset-2 focus:outline-none',
                themeClasses.closeButton,
              ]"
              aria-label="Закрыть модальное окно"
              @click="close"
            >
              <svg
                class="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <!-- Loading State -->
            <div
              v-if="loading"
              class="flex h-24 items-center justify-center"
            >
              <AppSpinner class="h-10 w-10" />
            </div>

            <!-- Content -->
            <div
              v-else
              :class="['flex flex-col', fullScreen ? 'h-full p-4 md:p-6' : 'max-h-full p-4 md:p-6']"
            >
              <!-- Title -->
              <h3
                v-if="slots.title"
                id="modal-title"
                :class="['mb-8 shrink-0 text-[24px]', 'select-none', themeClasses.title]"
              >
                <slot name="title" />
              </h3>

              <!-- Header -->
              <div
                v-if="slots.header"
                class="mb-4 shrink-0"
              >
                <slot name="header" />
              </div>

              <!-- Main Content -->
              <div class="min-h-0 flex-1">
                <slot />
              </div>

              <!-- Actions -->
              <div
                v-if="slots.actions"
                class="mt-6 shrink-0 pt-4"
                :class="[themeClasses.border]"
              >
                <slot name="actions" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
